import { createFileRoute, Link, Outlet, useMatches } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Badge, Input, Spinner, Table, Td, Th } from '@/components/admin/ui';
import {
  EmptyState,
  Pagination,
} from '@/components';
import { formatAdminDateTime } from '@/lib/format';
import { apiJson } from '@/lib/api';

type FeedbackStatus = 'new' | 'read' | 'archived';

type FeedbackListItem = {
  id: number;
  submitted_at: string;
  message_preview: string;
  status: FeedbackStatus;
  platform: string | null;
  app_version: string | null;
  installation_id: string;
  account_id: string | null;
  contact_email: string | null;
};

type ListRes = {
  items: FeedbackListItem[];
  total: number;
  page: number;
  page_size: number;
};

function statusBadge(status: FeedbackStatus) {
  if (status === 'new') return <Badge variant="primary">New</Badge>;
  if (status === 'archived') return <Badge variant="neutral">Archived</Badge>;
  return <Badge variant="neutral">Read</Badge>;
}

function FeedbackList() {
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [status, setStatus] = useState<FeedbackStatus | ''>('');
  const [page, setPage] = useState(1);
  const pageSize = 50;

  useEffect(() => {
    const t = setTimeout(() => {
      setQDebounced(q);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'feedback', qDebounced, status, page, pageSize],
    queryFn: () => {
      const params = new URLSearchParams();
      if (qDebounced.trim()) params.set('q', qDebounced.trim());
      if (status) params.set('status', status);
      params.set('page', String(page));
      params.set('page_size', String(pageSize));
      params.set('sort', 'submitted_at_desc');
      return apiJson<ListRes>(`/v1/admin/feedback?${params.toString()}`);
    },
  });

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold tracking-tight">Feedback</h1>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="min-w-0 flex-1">
          <Input
            label="Search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Message, email, or installation ID"
          />
        </div>
        <div>
          <label className="mb-2 block text-sm text-nova-muted-foreground">Status</label>
          <select
            className="w-full rounded-lg border border-nova-border h-8 px-2.5 text-sm"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as FeedbackStatus | '');
              setPage(1);
            }}
          >
            <option value="">All</option>
            <option value="new">New</option>
            <option value="read">Read</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>

      {isLoading && (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      )}
      {isError && (
        <p className="text-sm text-red-600">
          {(error as Error).message}
          <button type="button" className="ml-2 underline" onClick={() => void refetch()}>
            Retry
          </button>
        </p>
      )}
      {data && data.items.length === 0 && (
        <EmptyState title="No feedback yet" hint="Submissions from the app will appear here." />
      )}
      {data && data.items.length > 0 && (
        <>
          <div className="max-w-full overflow-x-auto">
            <Table>
              <thead>
                <tr>
                  <Th>Submitted</Th>
                  <Th>Status</Th>
                  <Th>Message</Th>
                  <Th>Platform</Th>
                  <Th>Contact</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={item.id}>
                    <Td>
                      <Link
                        to="/admin/feedback/$id"
                        params={{ id: String(item.id) }}
                        className="font-medium text-nova-primary hover:underline"
                      >
                        {formatAdminDateTime(item.submitted_at)}
                      </Link>
                    </Td>
                    <Td>{statusBadge(item.status)}</Td>
                    <Td className="max-w-md truncate">
                      <Link
                        to="/admin/feedback/$id"
                        params={{ id: String(item.id) }}
                        className="hover:underline"
                      >
                        {item.message_preview}
                      </Link>
                    </Td>
                    <Td>
                      {[item.platform, item.app_version].filter(Boolean).join(' · ') || '—'}
                    </Td>
                    <Td>{item.contact_email ?? '—'}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <Pagination page={page} pageSize={pageSize} total={data.total} onChange={setPage} />
        </>
      )}
    </div>
  );
}

/**
 * Parent for `/admin/feedback` (list) and `/admin/feedback/$id` (detail).
 * Without an Outlet, navigating to a feedback item keeps the list mounted.
 */
function FeedbackLayout() {
  const matches = useMatches();
  const leafId = matches.at(-1)?.routeId;
  if (leafId === '/admin/feedback/$id') {
    return <Outlet />;
  }
  return <FeedbackList />;
}

export const Route = createFileRoute('/admin/feedback')({
  component: FeedbackLayout,
});
